import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { fetchLeadActivity } from '../api/activity.js';
import { fetchLeadNotifications, markLeadNotificationsRead } from '../api/notifications';
import { uploadFile } from '../api/uploads.js';
import { sendEmail } from '../api/email.js';
import { getStageOneCampaignOptions } from '../api/outreach/campaigns.js';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { fetchLeadReminders } from '../api/reminders.js';
import apiClient from '../api/client.js';
import { LeadDetailsInfoTab } from './LeadDetailsInfoTab.jsx';
import { LeadDetailsMonitoringTab } from './LeadDetailsMonitoringTab.jsx';
import { LeadDetailsEmailTab } from './LeadDetailsEmailTab.jsx';
import { LeadDetailsDocsTab } from './LeadDetailsDocsTab.jsx';
import { LeadDetailsJournalTab } from './LeadDetailsJournalTab.jsx';
import { queryKeys } from '../lib/queryKeys';
import { useMessages } from '../hooks/useMessages';
import FunnelEnrollModal from './outreach/FunnelEnrollModal.jsx';
import { fetchCollaborations } from '../api/collaborations.js';
import { displayPrice } from '../lib/displayPrice.js';
import { isDisplayableMessage } from '../lib/messageDisplay.js';
import { NestedStatusDropdown } from './NestedStatusDropdown.jsx';
import { FilterDropdown } from './FilterDropdown.jsx';
import { GeoAutocomplete } from './GeoAutocomplete.jsx';
import { LeadStripTextField, LeadStripOptionField } from './LeadStripTextField.jsx';
import { FOUND_SOURCES, CONNECTION_TYPES } from '../../../shared/leadOptions/index.js';
import { Badge, Button, Icon } from './ds';
import {
    formatDateTime, getLeadTikTokInfo, normalizeHandle
} from '../utils.js';

const isEditorEmpty = (html) => !html || html.replace(/<[^>]*>/g, '').trim() === '';

const TAB_ITEMS = [
    { id: 'info', label: 'Инфо' },
    { id: 'monitoring', label: 'Статистика' },
    { id: 'email', label: 'Сообщения' },
    { id: 'docs', label: 'Документы' },
    { id: 'journal', label: 'Журнал' },
];
const RAIL_TAB_IDS = TAB_ITEMS.map(t => t.id);

/**
 * Normalises a deep-link tab id; App.jsx's URL sync uses it too. 'instagram' resolves to
 * the Сообщения tab on the Instagram channel and 'edit' to Инфо; anything unknown or
 * absent falls back to Инфо.
 *
 * @param {string|null|undefined} initialTab
 * @returns {{panelTab: string, msgChannel: 'email'|'ig'}}
 */
export function resolveInitialTab (initialTab) {
    if (initialTab === 'instagram') return { panelTab: 'email', msgChannel: 'ig' };
    if (initialTab === 'edit') return { panelTab: 'info', msgChannel: 'email' };
    if (RAIL_TAB_IDS.includes(initialTab)) return { panelTab: initialTab, msgChannel: 'email' };
    return { panelTab: 'info', msgChannel: 'email' };
}

const initialsOf = (value) => {
    const parts = String(value || '').replace(/^@+/, '').split(/[\s._-]+/).filter(Boolean);
    if (parts.length === 0) return '—';
    return parts.slice(0, 2).map(p => p[0]).join('').toUpperCase();
};

const flagOf = (code) => {
    if (typeof code !== 'string' || !/^[A-Za-z]{2}$/.test(code)) return null;
    return String.fromCodePoint(
        ...code.toUpperCase().split('').map(ch => 0x1F1E6 + ch.charCodeAt(0) - 65),
    );
};


export const LeadDetailsModal = ({
    open,
    lead,
    statuses = [],
    statusLookup,
    onClose,
    onDelete,
    currentUser,
    managers = [],
    onAssignOwner,
    onStatusChange,
    ownerUpdating = false,
    deleting = false,
    timer = null,
    onTimerExpire,
    incomingActivity = null,
    onEditSubmit,
    initialTab = 'info',
}) => {
    const queryClient = useQueryClient();
    const messagingOn = currentUser?.messagingEnabled === true;
    const canUseOutreach = messagingOn && ['ADMIN', 'TEAM_LEAD', 'ASSISTANT'].includes(currentUser?.role);
    const canSendEmail = canUseOutreach && lead?.canWrite === true;
    const outreachAccess = useRef({});
    const outreachKey = `${currentUser?.id}:${currentUser?.role}:${lead?.id}:${open}`;
    outreachAccess.current = { allowed: canUseOutreach, canSend: canSendEmail, key: outreachKey };
    useEffect(() => () => { outreachAccess.current.allowed = false; outreachAccess.current.canSend = false; }, []);
    const { data: messages = [] } = useMessages(messagingOn ? lead?.id : null);

    const { data: leadReminders = [] } = useQuery({
        queryKey: queryKeys.reminders.byLead(lead?.id),
        queryFn: () => fetchLeadReminders(lead.id),
        enabled: !!lead?.id && open,
    });

    useEffect(() => {
        const resolved = resolveInitialTab(initialTab);
        setPanelTab(resolved.panelTab);
        setMsgChannel(resolved.msgChannel);
    }, [lead?.id, initialTab]);

    const [stageOneError, setStageOneError] = useState(null);
    const [stageOneInfo, setStageOneInfo] = useState('');
    const [stageOneCampaignOptions, setStageOneCampaignOptions] = useState([]);
    const [stageOneCampaignError, setStageOneCampaignError] = useState(null);
    const [emailBodyDraft, setEmailBodyDraft] = useState('');
    const [emailHtmlOverride, setEmailHtmlOverride] = useState(null);
    const [emailComposerMode, setEmailComposerMode] = useState('visual');
    const [emailSending, setEmailSending] = useState(false);
    const [showTemplatePicker, setShowTemplatePicker] = useState(false);
    const [myTemplates, setMyTemplates] = useState([]);
    const [templatesLoaded, setTemplatesLoaded] = useState(false);
    const [emailAttachment, setEmailAttachment] = useState(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [attachUploading, setAttachUploading] = useState(false);
    const [emailSendError, setEmailSendError] = useState(null);
    const [replyToMessage, setReplyToMessage] = useState(null);
    const [rawPanelTab, setPanelTab] = useState(() => resolveInitialTab(initialTab).panelTab);
    const panelTab = rawPanelTab === 'email' && !messagingOn ? 'info' : rawPanelTab;
    const [msgChannel, setMsgChannel] = useState(() => resolveInitialTab(initialTab).msgChannel);
    const [monitoringPackId, setMonitoringPackId] = useState(null);
    const [editMode, setEditMode] = useState(false);

    useEffect(() => {
        if (!lead?.id || !open || panelTab !== 'email') return;
        apiClient.patch(`/leads/${lead.id}/mark-read`)
            .then(() => {
                queryClient.invalidateQueries({ queryKey: queryKeys.leads.unreadStatus() });
                queryClient.invalidateQueries({ queryKey: queryKeys.inbox.all });
            })
            .catch(() => {});
        markLeadNotificationsRead(lead.id, ['inbound_message'])
            .then(() => queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all }))
            .catch(() => {});
    }, [lead?.id, panelTab, open]);
    const [activityItems, setActivityItems] = useState([]);
    const [activityLoading, setActivityLoading] = useState(false);
    const [activityError, setActivityError] = useState(null);
    const [leadDuplicates, setLeadDuplicates] = useState([]);
    const [funnelEnrollModalOpen, setFunnelEnrollModalOpen] = useState(false);
    const [isSyncingCampaigns, setIsSyncingCampaigns] = useState(false);
    useEffect(() => {
        if (!canUseOutreach) {
            setStageOneCampaignOptions([]);
            setStageOneCampaignError(null);
            setFunnelEnrollModalOpen(false);
            setReplyToMessage(null);
        }
    }, [canUseOutreach]);

    const { data: leadCollabs = [] } = useQuery({
        queryKey: ['lead-collabs', lead?.id],
        queryFn: () => fetchCollaborations(lead.id),
        enabled: !!lead?.id && open,
    });
    const dp = displayPrice(leadCollabs);

    const { data: leadNotif } = useQuery({
        queryKey: ['notifications', 'lead', lead?.id],
        queryFn: () => fetchLeadNotifications(lead.id),
        enabled: Boolean(lead?.id),
    });
    const unreadCount = (leadNotif?.notifications ?? []).filter((n) => !n.readAt).length;

    const chatScrollRef = useRef(null);
    const logScrollRef = useRef(null);
    const tiktokInfo = useMemo(() => getLeadTikTokInfo(lead), [lead]);
    const hasEmailChannel = true;
    const isTestLead = Boolean(lead?.metadata?.testLead);
    const visibleMessages = useMemo(() => {
        if (!isTestLead) return messages;
        const hasInbound = messages.some(message => message?.direction === 'in');
        if (!hasInbound) {
            return messages.filter(message => message?.direction === 'in');
        }
        return messages;
    }, [isTestLead, messages]);

    const replyEmailSubject = useMemo(() => {
        let raw = '';
        if (replyToMessage) {
            raw = replyToMessage.payload?.subject || '';
        } else {
            const lastInbound = [...messages]
                .reverse()
                .find(m => m.channel === 'email' && m.direction === 'in');
            raw = lastInbound?.payload?.subject || '';
        }
        if (!raw) {
            const lastOutbound = [...messages]
                .reverse()
                .find(m => m.channel === 'email' && m.direction === 'out');
            raw = lastOutbound?.payload?.subject || '';
        }
        if (!raw) return null;
        return raw.toLowerCase().startsWith('re:') ? raw : `Re: ${raw}`;
    }, [messages, replyToMessage]);
    const orderedActivityItems = useMemo(() => {
        return [...activityItems].sort((a, b) => {
            const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
            return aTime - bTime;
        });
    }, [activityItems]);

    useEffect(() => {
        if (!lead?.id) { setLeadDuplicates([]); return; }
        fetch(`/api/leads/${lead.id}/duplicates`)
            .then(r => r.json())
            .then(data => setLeadDuplicates(data.ok && Array.isArray(data.leads) ? data.leads : []))
            .catch(() => setLeadDuplicates([]));
    }, [lead?.id, lead?.duplicateOfId]);

    useEffect(() => {
        setStageOneError(null);
        setStageOneInfo('');
        setStageOneCampaignOptions([]);
        setStageOneCampaignError(null);
        setEmailBodyDraft('');
        setEmailHtmlOverride(null);
        setEmailComposerMode('visual');
        setEmailSending(false);
        setEmailSendError(null);
        setEditMode(false);
        prevMessageCountRef.current = -1;
    }, [lead?.id, lead?.email, open]);

    const [geoDraft, setGeoDraft] = useState(lead?.geo || '');
    const geoDraftRef = useRef(lead?.geo || '');
    const geoCommitRef = useRef(null);
    useEffect(() => {
        setGeoDraft(lead?.geo || '');
        geoDraftRef.current = lead?.geo || '';
    }, [lead?.id, lead?.geo]);
    useEffect(() => () => { if (geoCommitRef.current) clearTimeout(geoCommitRef.current); }, []);

    const refreshStageOneOptions = useCallback(async (leadId) => {
        if (!leadId || !outreachAccess.current.allowed) return;
        const key = outreachAccess.current.key;
        setStageOneCampaignError(null);
        try {
            const result = await getStageOneCampaignOptions(leadId);
            if (!outreachAccess.current.allowed || outreachAccess.current.key !== key) return;
            const items = Array.isArray(result?.items) ? result.items : [];
            setStageOneCampaignOptions(items);
        } catch (err) {
            if (outreachAccess.current.allowed && outreachAccess.current.key === key) setStageOneCampaignError(err?.data?.error || err?.message || 'Не удалось загрузить кампании');
        }
    }, []);

    useEffect(() => {
        if (!canUseOutreach || !open || !lead?.id || !hasEmailChannel) return;
        refreshStageOneOptions(lead.id);
    }, [open, lead?.id, hasEmailChannel, canUseOutreach, outreachKey, refreshStageOneOptions]);

    const prevMessageCountRef = useRef(-1);
    useEffect(() => {
        if (!canUseOutreach || !open || !lead?.id || !hasEmailChannel) return;
        if (prevMessageCountRef.current === -1) {
            prevMessageCountRef.current = messages.length;
            return;
        }
        if (messages.length === prevMessageCountRef.current) return;
        prevMessageCountRef.current = messages.length;
        // The Reply.io webhook may not have updated the lead's status yet.
        const timer = setTimeout(() => {
            refreshStageOneOptions(lead.id);
        }, 3000);
        return () => clearTimeout(timer);
    }, [open, lead?.id, hasEmailChannel, messages.length, canUseOutreach, outreachKey, refreshStageOneOptions]);

    useEffect(() => {
        if (!open || !lead?.id) {
            setActivityItems([]);
            setActivityError(null);
            return;
        }
        let cancelled = false;
        const fetchActivity = async () => {
            setActivityLoading(true);
            setActivityError(null);
            try {
                const items = await fetchLeadActivity(lead.id);
                if (!cancelled) {
                    setActivityItems(items);
                }
            } catch (err) {
                if (!cancelled) {
                    setActivityError(err?.data?.error || 'Не удалось загрузить журнал');
                }
            } finally {
                if (!cancelled) {
                    setActivityLoading(false);
                }
            }
        };
        fetchActivity();
        return () => {
            cancelled = true;
        };
    }, [open, lead?.id]);

    useEffect(() => {
        if (!incomingActivity || !lead?.id) return;
        if (incomingActivity.leadId !== lead.id) return;
        const activity = incomingActivity.activity;
        if (!activity) return;
        setActivityItems(prev => {
            const alreadyExists = prev.some(item => {
                if (item?.id && activity.id) {
                    return item.id === activity.id;
                }
                if (item?.createdAt && activity.createdAt) {
                    return item.createdAt === activity.createdAt && item.type === activity.type;
                }
                return false;
            });
            if (alreadyExists) return prev;
            return [...prev, activity];
        });
    }, [incomingActivity, lead?.id]);

    const orderedMessages = useMemo(() => {
        return [...visibleMessages].sort((a, b) => {
            const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
            return aTime - bTime;
        });
    }, [visibleMessages]);

    const resolveMessageChannel = useCallback((message) => {
        if (!message) return 'instagram';
        const channel = String(message.channel || '').toLowerCase();
        const provider = String(message.provider || message.source || '').toLowerCase();
        if (channel === 'email' || provider.includes('email') || provider.includes('instantly') || provider.includes('replyio')) return 'email';
        return 'instagram';
    }, []);

    const shownMessages = useMemo(() => {
        if (panelTab === 'email') {
            return msgChannel === 'ig'
                ? orderedMessages.filter(m => resolveMessageChannel(m) === 'instagram')
                : orderedMessages.filter(m => resolveMessageChannel(m) === 'email' && isDisplayableMessage(m));
        }
        return orderedMessages;
    }, [orderedMessages, panelTab, msgChannel, resolveMessageChannel]);
    const isOutboundStartedStatus = useCallback((statusValue) => {
        const status = String(statusValue || '').toUpperCase();
        return ['SENT', 'OPENED', 'RESPONDED', 'COMPLETED', 'INTERESTED'].includes(status);
    }, []);
    const enrolledCampaigns = useMemo(() => {
        return stageOneCampaignOptions.filter(item => item.isEnrolled);
    }, [stageOneCampaignOptions]);
    const hasEmailThread = useMemo(() => {
        return orderedMessages.some(message => resolveMessageChannel(message) === 'email');
    }, [orderedMessages, resolveMessageChannel]);
    const hasSentInCampaign = useMemo(() => {
        return enrolledCampaigns.some(item => isOutboundStartedStatus(item?.recipientStatus));
    }, [enrolledCampaigns, isOutboundStartedStatus]);
    const canManualEmailReply = canSendEmail && (hasEmailThread || hasSentInCampaign);
    const formatCampaignProgress = useCallback((statusValue) => {
        const status = String(statusValue || '').toUpperCase();
        if (!status || status === 'NOT_ENROLLED') return 'Не в кампании';
        if (status === 'REMOVED') return 'Удалён из кампании';
        if (status === 'PENDING' || status === 'QUEUED') return 'В очереди';
        if (status === 'SENT') return 'Отправлено';
        if (status === 'OPENED') return 'Открыто';
        if (status === 'CLICKED' || status === 'LINK_CLICKED') return 'Ссылка открыта';
        if (status === 'RESPONDED') return 'Ответил';
        if (status === 'INTERESTED') return 'Interested';
        if (status === 'REPLY_RECEIVED') return 'Reply received';
        if (status === 'COMPLETED') return 'Completed';
        if (status === 'SUBSEQUENCE') return 'Subsequence';
        if (status === 'FAILED' || status === 'BOUNCED') return 'Ошибка доставки';
        if (status === 'UNSUBSCRIBED') return 'Отписался';
        if (status === 'OUT_OF_OFFICE') return 'Out of office';
        if (status === 'NOT_INTERESTED') return 'Not interested';
        if (status === 'WRONG_PERSON') return 'Wrong person';
        return statusValue;
    }, []);

    useEffect(() => {
        if (!open) return;
        const container = chatScrollRef.current;
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    }, [open, lead?.id, shownMessages.length]);

    useEffect(() => {
        if (!open) return;
        const container = logScrollRef.current;
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    }, [open, lead?.id, orderedActivityItems.length]);

    useEffect(() => {
        setStageOneError(null);
        setStageOneInfo('');
        setSelectedTemplateId(null);
    }, [lead?.id]);

    const avatarSrc = useMemo(() => {
        const url = lead?.avatarUrl;
        if (!url) return null;
        if (!/^https?:\/\//i.test(url)) return url;
        if (!lead?.id) return null;
        const params = new URLSearchParams();
        const ts = lead?.updatedAt ? new Date(lead.updatedAt).getTime() : Number.NaN;
        if (!Number.isNaN(ts)) params.set('v', String(ts));
        const query = params.toString();
        return `/api/media/avatars/${lead.id}${query ? `?${query}` : ''}`;
    }, [lead?.avatarUrl, lead?.id, lead?.updatedAt]);
    const leadInitials = useMemo(
        () => initialsOf(lead?.name || lead?.realName || ''),
        [lead?.name, lead?.realName],
    );
    const geoFlag = flagOf(lead?.geoCountry?.code);

    const submitLeadFields = useCallback((fields) => {
        if (!onEditSubmit || !lead) return undefined;
        const meta = lead.metadata || {};
        return onEditSubmit({
            negotiationMin: meta.negotiationPriceMin ?? meta.negotiation?.min ?? null,
            negotiationMax: meta.negotiationPriceMax ?? meta.negotiation?.max ?? null,
            payment: meta.payment ?? null,
            ...fields,
        });
    }, [onEditSubmit, lead]);
    const handleGeoChange = useCallback((value) => {
        geoDraftRef.current = value;
        setGeoDraft(value);
    }, []);
    const commitGeo = useCallback(() => {
        const next = (geoDraftRef.current || '').trim();
        if (next === (lead?.geo || '')) return;
        submitLeadFields({ geo: next || null });
    }, [lead?.geo, submitLeadFields]);
    const handleGeoBlur = useCallback(() => {
        if (geoCommitRef.current) clearTimeout(geoCommitRef.current);
        geoCommitRef.current = setTimeout(commitGeo, 250);
    }, [commitGeo]);
    const handleToggleEdit = useCallback(() => setEditMode(v => !v), []);
    const handleSaveEdit = useCallback(() => setEditMode(false), []);
    const handleCancelEdit = useCallback(() => setEditMode(false), []);

    if (!open || !lead) return null;

    const buildEmailAttachmentHtml = (url, originalName, mimeType) => {
        if (mimeType && mimeType.startsWith('image/')) {
            return `<br><br><img src="${url}" alt="${originalName}" style="max-width:560px;border-radius:4px;">`;
        }
        return `<br><br>📎 <a href="${url}">${originalName}</a>`;
    };

    const handleEmailSendInternal = async () => {
        if (!canSendEmail) return;
        const sendKey = outreachAccess.current.key;
        if (!lead?.id || !lead?.email) return;
        if (!emailHtmlOverride && isEditorEmpty(emailBodyDraft) && !emailAttachment) return;
        setEmailSendError(null);
        setEmailSending(true);
        try {
            // The email provider resolves {{…}} variables such as {{accountSignature}} and {{firstName}}.
            const sourceHtml = emailHtmlOverride || emailBodyDraft;
            let finalHtml = sourceHtml;
            if (emailAttachment) {
                setAttachUploading(true);
                const uploaded = await uploadFile(emailAttachment.file);
                setAttachUploading(false);
                finalHtml = finalHtml + buildEmailAttachmentHtml(uploaded.url, uploaded.originalName, uploaded.mimeType);
            }
            let replyToUuid = null;
            if (replyToMessage?.externalId) {
                const eid = replyToMessage.externalId;
                replyToUuid = eid.startsWith('instantly-email:') ? eid.replace('instantly-email:', '') : eid;
            }
            if (!outreachAccess.current.canSend || outreachAccess.current.key !== sendKey) return;
            await sendEmail({
                leadId: lead.id,
                to: [lead.email],
                html: finalHtml,
                ...(replyEmailSubject ? { subject: replyEmailSubject } : {}),
                ...(replyToUuid ? { replyToUuid } : {}),
                ...(selectedTemplateId ? { templateId: selectedTemplateId } : {})
            });
            setEmailBodyDraft('');
            setEmailHtmlOverride(null);
            setEmailAttachment(null);
            setReplyToMessage(null);
            setSelectedTemplateId(null);
            queryClient.invalidateQueries({ queryKey: queryKeys.messages.byLead(lead.id) });
            queryClient.invalidateQueries({ queryKey: ['outreach'] });
        } catch (err) {
            setAttachUploading(false);
            const msg = err?.data?.error || err?.message || 'Не удалось отправить email';
            setEmailSendError(msg);
            throw new Error(msg);
        } finally {
            setEmailSending(false);
        }
    };

    const handleEmailSendSubmit = async (event) => {
        if (event) event.preventDefault();
        if ((!emailHtmlOverride && !emailBodyDraft.trim() && !emailAttachment) || emailSending) return;
        try {
            await handleEmailSendInternal();
        } catch (_) {
        }
    };

    const emailComposerError = stageOneError || null;
    const isAdminView = currentUser?.role === 'ADMIN';
    const instagramProfileUrl = useMemo(() => {
        const raw = String(lead?.instagram || '').trim();
        if (!raw) return null;
        if (/^https?:\/\//i.test(raw)) return raw;
        const handle = raw.replace(/^@+/, '').split(/[/?#]/)[0];
        if (!handle) return null;
        return `https://www.instagram.com/${handle}/`;
    }, [lead?.instagram]);

    const instagramHandle = useMemo(
        () => normalizeHandle(lead?.instagram, 'instagram'),
        [lead?.instagram]
    );
    const chanAddress = useMemo(() => (
        msgChannel === 'ig'
            ? (instagramHandle ? `@${instagramHandle}` : null)
            : (String(lead?.email || '').trim() || null)
    ), [msgChannel, instagramHandle, lead?.email]);

    const managerValue = isAdminView
        ? (
            <FilterDropdown
                value={lead.ownerId || 'none'}
                onChange={(val) => onAssignOwner?.(lead.id, val === 'none' ? null : val)}
                placeholder='Не назначен'
                className='oc-lead-card__strip-dd'
                triggerClassName='oc-lead-card__strip-trigger'
                options={[
                    { value: 'none', label: 'Без менеджера', dot: true, dotStyle: { background: 'var(--color-warning)' } },
                    ...managers.map(m => ({
                        value: m.id,
                        label: m.name || m.email,
                        dot: true,
                        dotStyle: { background: 'var(--color-info)' },
                    })),
                ]}
            />
        )
        : (lead.owner?.name || lead.owner?.email || '—');

    const statusValue = (
        <NestedStatusDropdown
            value={lead.statusId || lead.status || null}
            statuses={statuses}
            onChange={(newSubId) => onStatusChange?.(lead.id, newSubId)}
            placeholder='—'
            disabled={!onStatusChange}
        />
    );
    const donorLabel = lead.isDuplicate ? 'дубль' : 'оригинал';
    const donorTone = lead.isDuplicate ? 'duplicate' : 'original';
    const leadTags = Array.isArray(lead.tags) ? lead.tags : [];
    const showSummaryStrip = panelTab === 'info' || panelTab === 'docs';

    return (
        <>
        <div className='details-modal-backdrop outcrm-scope oc-lead-card'>
            <div className='details-modal oc-lead-card__dialog oc-lead-card__dialog--drawer' role='dialog'>
                <div className='details-panel__header oc-lead-card__header'>
                    <div className='oc-lead-card__header-lead'>
                        <span className='oc-lead-card__avatar'>
                            {avatarSrc
                                ? <img className='oc-lead-card__avatar-img' src={avatarSrc} alt='' loading='lazy' decoding='async' />
                                : <span className='oc-lead-card__avatar-initials'>{leadInitials}</span>}
                        </span>
                        <h2 className='oc-lead-card__header-name'>{lead.name}</h2>
                        {instagramProfileUrl && (
                            <Icon name='instagram-logo' size='md' className='oc-lead-card__header-glyph' title='Instagram' />
                        )}
                        {lead.realName && (
                            <span className='oc-lead-card__greet' title='Имя для обращения в переписке'>
                                <Icon name='user' size='sm' />
                                {lead.realName}
                            </span>
                        )}
                        {geoFlag && (
                            <span className='oc-lead-card__flag' title={lead.geoCountry?.nameRu || lead.geoCountry?.name || ''}>{geoFlag}</span>
                        )}
                        {leadTags.map(tag => (
                            <span key={tag.id} className='oc-lead-card__chip'>{tag.label}</span>
                        ))}
                    </div>
                    <div className='oc-lead-card__header-actions'>
                        {panelTab === 'info' && (
                            <Button
                                hierarchy='tertiary'
                                size='small'
                                iconOnly
                                className={`oc-lead-card__editbtn${editMode ? ' oc-lead-card__editbtn--on' : ''}`}
                                aria-label='Редактировать карточку'
                                title='Редактировать карточку'
                                onClick={handleToggleEdit}
                            >
                                <Icon name='pencil-simple' size='sm' />
                            </Button>
                        )}
                        {unreadCount > 0 && (
                            <Badge color='red' type='contained' size='small' title='Непрочитанные уведомления'>! {unreadCount}</Badge>
                        )}
                        <Button
                            hierarchy='tertiary'
                            size='small'
                            iconOnly
                            className='oc-lead-card__close'
                            aria-label='Закрыть'
                            onClick={onClose}
                        >
                            <Icon name='x' size='sm' />
                        </Button>
                    </div>
                </div>

                <div className='details-panel__tabs oc-lead-card__tabs'>
                    {TAB_ITEMS.filter(tab => messagingOn || tab.id !== 'email').map(tab => (
                        <button
                            key={tab.id}
                            className={`oc-lead-card__tab${panelTab === tab.id ? ' oc-lead-card__tab--active' : ''}`}
                            type='button'
                            onClick={() => { setMonitoringPackId(null); setPanelTab(tab.id); }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {showSummaryStrip && (
                    <div className='oc-lead-card__strip'>
                        <div className='oc-lead-card__strip-row'>
                            {statusValue}
                            <span className={`oc-lead-card__tag-donor oc-lead-card__tag-donor--${donorTone}`}>{donorLabel}</span>
                            {leadTags.map(tag => (
                                <span key={tag.id} className='oc-lead-card__chip'>{tag.label}</span>
                            ))}
                        </div>
                        <div className='oc-lead-card__strip-row oc-lead-card__strip-row--fields'>
                            <span className='oc-lead-card__strip-field'>
                                <Icon name='user-circle' size='sm' />
                                <span className='oc-lead-card__strip-label'>Менеджер</span>
                                {isAdminView
                                    ? managerValue
                                    : <span className='oc-lead-card__value'>{lead.owner?.name || lead.owner?.email || '—'}</span>}
                            </span>
                            <LeadStripOptionField
                                icon='compass'
                                label='Источник'
                                options={FOUND_SOURCES}
                                value={lead.metadata?.foundSource}
                                onPick={(v) => submitLeadFields({ foundSource: v })}
                            />
                            <LeadStripOptionField
                                icon='chat-circle-dots'
                                label='Способ связи'
                                options={CONNECTION_TYPES}
                                value={lead.metadata?.connectionType}
                                onPick={(v) => submitLeadFields({ connectionType: v })}
                            />
                            <LeadStripTextField
                                icon='tag'
                                label='Ниша'
                                value={lead.metadata?.niche}
                                onCommit={(v) => submitLeadFields({ niche: v })}
                            />
                            <span className='oc-lead-card__strip-field oc-lead-card__strip-geo' onBlur={handleGeoBlur}>
                                <Icon name='map-pin' size='sm' />
                                <span className='oc-lead-card__strip-label'>Гео</span>
                                <GeoAutocomplete value={geoDraft} onChange={handleGeoChange} />
                            </span>
                        </div>
                    </div>
                )}

                <div className='details-modal__body oc-lead-card__body'>
                    <LeadDetailsInfoTab
                        panelTab={panelTab}
                        lead={lead}
                        onClose={onClose}
                        instagramProfileUrl={instagramProfileUrl}
                        tiktokInfo={tiktokInfo}
                        queryClient={queryClient}
                        leadCollabs={leadCollabs}
                        leadDuplicates={leadDuplicates}
                        leadReminders={leadReminders}
                        canUseOutreach={canUseOutreach}
                        messagingOn={messagingOn}
                        isSyncingCampaigns={isSyncingCampaigns}
                        setIsSyncingCampaigns={setIsSyncingCampaigns}
                        setFunnelEnrollModalOpen={setFunnelEnrollModalOpen}
                        onOpenTab={(tab, ch, packId) => { if (ch) setMsgChannel(ch); setMonitoringPackId(packId ?? null); setPanelTab(tab); }}
                        editMode={editMode}
                        onSaveEdit={handleSaveEdit}
                        onCancelEdit={handleCancelEdit}
                        onEditSubmit={onEditSubmit}
                        onFieldsSubmit={submitLeadFields}
                    />

                    <LeadDetailsMonitoringTab
                        panelTab={panelTab}
                        lead={lead}
                        initialPackId={monitoringPackId}
                    />

                    <LeadDetailsEmailTab
                        leadId={lead.id}
                        panelTab={panelTab}
                        msgChannel={msgChannel}
                        setMsgChannel={setMsgChannel}
                        chanAddress={chanAddress}
                        resolveMessageChannel={resolveMessageChannel}
                        shownMessages={shownMessages}
                        chatScrollRef={chatScrollRef}
                        timer={timer}
                        onTimerExpire={onTimerExpire}
                        setReplyToMessage={setReplyToMessage}
                        hasEmailChannel={hasEmailChannel}
                        canSendEmail={canSendEmail}
                        canManualEmailReply={canManualEmailReply}
                        handleEmailSendSubmit={handleEmailSendSubmit}
                        templatesLoaded={templatesLoaded}
                        setTemplatesLoaded={setTemplatesLoaded}
                        myTemplates={myTemplates}
                        setMyTemplates={setMyTemplates}
                        showTemplatePicker={showTemplatePicker}
                        setShowTemplatePicker={setShowTemplatePicker}
                        setEmailHtmlOverride={setEmailHtmlOverride}
                        setEmailBodyDraft={setEmailBodyDraft}
                        emailAttachment={emailAttachment}
                        setEmailAttachment={setEmailAttachment}
                        selectedTemplateId={selectedTemplateId}
                        setSelectedTemplateId={setSelectedTemplateId}
                        replyToMessage={replyToMessage}
                        replyEmailSubject={replyEmailSubject}
                        emailHtmlOverride={emailHtmlOverride}
                        emailComposerMode={emailComposerMode}
                        setEmailComposerMode={setEmailComposerMode}
                        emailSending={emailSending}
                        emailBodyDraft={emailBodyDraft}
                        attachUploading={attachUploading}
                        enrolledCampaigns={enrolledCampaigns}
                        formatCampaignProgress={formatCampaignProgress}
                        hasSentInCampaign={hasSentInCampaign}
                        stageOneInfo={stageOneInfo}
                        emailComposerError={emailComposerError}
                        emailSendError={emailSendError}
                        stageOneCampaignError={stageOneCampaignError}
                    />

                    <LeadDetailsDocsTab
                        panelTab={panelTab}
                        lead={lead}
                    />

                    <LeadDetailsJournalTab
                        panelTab={panelTab}
                        lead={lead}
                        logScrollRef={logScrollRef}
                        activityItems={activityItems}
                        activityLoading={activityLoading}
                        activityError={activityError}
                        orderedActivityItems={orderedActivityItems}
                        managers={managers}
                        statusLookup={statusLookup}
                    />
                </div>
            </div>
        </div>

        {canUseOutreach && funnelEnrollModalOpen && lead && (
            <FunnelEnrollModal
                isOpen={funnelEnrollModalOpen}
                onClose={() => setFunnelEnrollModalOpen(false)}
                leadIds={[lead.id]}
                leads={[{ id: lead.id, name: lead.name || lead.realName, email: lead.email, instagram: lead.instagram }]}
            />
        )}
        </>
    );
};
